import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, CheckCircle, X, AlertCircle,
  Trash2, Clock, Calendar, Info,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import RecurrenceProductResultCard from './RecurrenceProductResultCard';
import { TimeSlotTips } from '../components/helpers/TimeSlotHelpers';
import {
  formatCurrency as formatSharedCurrency,
  formatNumber as formatSharedNumber
} from '../../../../../utils/format';

const formatDate = (d) =>
  d ? moment(d).format('MMM DD, YYYY') : 'Invalid date';

const isBookableDate = (dateStr, workingDays = [], holidays = [], timezone = 'America/New_York') => {
  if (!dateStr) return true;

  const dateMom = moment.tz(dateStr, 'YYYY-MM-DD', timezone);

  const isHoliday = holidays.some((h) => {
    const hDate = typeof h === 'string' ? h : h?.date;
    return hDate && moment(hDate).isSame(dateMom, 'day');
  });
  if (isHoliday) return false;

  if (workingDays.length > 0) {
    const dayName = dateMom.format('dddd').toUpperCase();
    if (!workingDays.includes(dayName)) return false;
  }

  return true;
};

const ReviewSchedule = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    product                = null,
    searchCriteria         = {},
    selectedTime           = null,
    recurrenceData         = null,
    occurrenceDates: initialOccurrenceDates   = [],
    availabilityData: initialAvailabilityData = [],
    returnTo               = 'selected-product-details',
    originalProductData    = null,
    updatedTimes: initialUpdatedTimes = {},
  } = location.state || {};

  const [occurrenceDates, setOccurrenceDates] = useState(
    Array.isArray(initialOccurrenceDates) ? initialOccurrenceDates : [],
  );
  const [availabilityData, setAvailabilityData] = useState(
    Array.isArray(initialAvailabilityData) ? initialAvailabilityData : [],
  );
  const [updatedTimes, setUpdatedTimes]         = useState(initialUpdatedTimes || {});
  const [clearedDates, setClearedDates]         = useState(new Set());
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [showBookingModal, setShowBookingModal]   = useState(false);
  const [showSuccessToast, setShowSuccessToast]   = useState(false);
  const [isProcessing, setIsProcessing]           = useState(false);
  const [error, setError]                         = useState(null);
  const [bookingError, setBookingError]           = useState('');
  const [showTimeMissingWarning, setShowTimeMissingWarning] = useState(false);
  const [removedSessions, setRemovedSessions]     = useState([]);
  const [showConflictDates, setShowConflictDates] = useState(false);

  const selectedCompanyState = useSelector((s) => s?.companyReducer?.company);
  const companyState = useMemo(() => selectedCompanyState || {}, [selectedCompanyState]);
  const formatCurrency = useCallback(
    (amount) => formatSharedCurrency(amount, companyState),
    [companyState],
  );
  const formatNumber = useCallback(
    (value) => formatSharedNumber(value, companyState),
    [companyState],
  );
  const workingDays = useMemo(
    () => companyState?.workingSchedule?.workingDays || [],
    [companyState?.workingSchedule?.workingDays],
  );
  const holidays = useMemo(
    () => companyState?.workingSchedule?.holidays || [],
    [companyState?.workingSchedule?.holidays],
  );
  const slotIntervalInMinutes = companyState?.workingSchedule?.slotIntervalInMinutes || 60;

  const searchPeriod    = searchCriteria?.searchPeriod || {};
  const effectiveDate   = searchPeriod?.effectiveDate  || {};
  const localStartDate  = effectiveDate?.localStartDate;
  const localEndDate    = effectiveDate?.localEndDate;
  const timeZone        = effectiveDate?.timeZone || 'America/New_York';

  useEffect(() => {
    if (!product)                  setError('No product data found');
    else if (!occurrenceDates.length) setError('No occurrence dates found');
    else                           setError(null);
  }, [product, occurrenceDates]);

  useEffect(() => {
    if (showTimeMissingWarning) {
      const timer = setTimeout(() => {
        setShowTimeMissingWarning(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTimeMissingWarning]);

  const handleBack = () => {
    const state = {
      product, searchCriteria, selectedTime,
      recurrenceData: recurrenceData
        ? { ...recurrenceData, occurrences: occurrenceDates.length }
        : null,
      updatedTimes, occurrenceDates, availabilityData,
      fromReviewSchedule: true,
    };
    if (returnTo === 'selected-product-details' && originalProductData) {
      navigate('/selected-product-details', { state });
    } else {
      navigate(-1);
    }
  };

  const handleClearSelection = (date) => {
    setClearedDates(prev => new Set(prev).add(date));
    setUpdatedTimes(prev => {
      const next = { ...prev };
      delete next[date];
      return next;
    });
  };

  const handleSelectProduct = (selectedProduct, selectedDate) => {
    if (!selectedProduct?.selectedTime) return;
    setUpdatedTimes((prev) => ({
      ...prev,
      [selectedDate]: {
        start:      selectedProduct.selectedTime.start,
        end:        selectedProduct.selectedTime.end,
        duration:   selectedProduct.selectedTime.duration,
        totalPrice: selectedProduct.totalPrice,
      },
    }));
    setAvailabilityData((prev) => {
      const next = [...prev];
      const index = occurrenceDates.indexOf(selectedDate);
      if (index >= 0) {
        next[index] = {
          ...(next[index] || {}),
          available: true,
          conflictingSlots: [],
        };
      }
      return next;
    });
    setClearedDates(prev => {
      const next = new Set(prev);
      next.delete(selectedDate);
      return next;
    });
    setShowTimeMissingWarning(false);
  };

  const handleDeleteOccurrence = (date, index) => {
    const removedSessionNumber = index + 1;

    setOccurrenceDates((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setAvailabilityData((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setUpdatedTimes((prev) => {
      const next = { ...prev };
      delete next[date];
      return next;
    });
    setClearedDates(prev => {
      const next = new Set(prev);
      next.delete(date);
      return next;
    });
    setRemovedSessions((prev) => [
      ...prev,
      { date, occurrenceNumber: removedSessionNumber },
    ]);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const scheduleData = useMemo(() => {
    if (!product || !Array.isArray(occurrenceDates) || occurrenceDates.length === 0) return [];

    return occurrenceDates.map((date, index) => {
      const availability = availabilityData[index] ?? null;
      const isAvailable  = availability?.available !== false;
      const wasCleared = clearedDates.has(date);
      let timeToUse = updatedTimes[date];
      
      if (!wasCleared && !timeToUse) {
        timeToUse = selectedTime;
      }

      const isBookable = isBookableDate(date, workingDays, holidays, timeZone);

      const scheduleProduct = {
        ...product,
        primaryId:   product.primaryId ? `${product.primaryId}-${index + 1}` : `product-${index + 1}`,
        originalPrimaryId: product.primaryId,
        displayDate: date ? moment(date).format('MMM DD, YYYY') : 'Invalid Date',
        dateIndex:   index + 1,
        slots:       availability?.productData?.slots || product.slots || [],
      };

      return {
        product:        scheduleProduct,
        searchCriteria: { ...searchCriteria, date, displayDate: moment(date).format('MMM DD, YYYY'), timeZone },
        selectedTime:   timeToUse,
        uniqueKey:      `${product.primaryId || 'p'}-${date}-${index}`,
        isAvailable:    isAvailable && isBookable,
        isBookable,
        availabilityInfo: availability,
        wasCleared,
      };
    });
  }, [
    product, occurrenceDates, availabilityData,
    updatedTimes, selectedTime, searchCriteria,
    timeZone, workingDays, holidays, clearedDates,
  ]);

  const availableCount   = scheduleData.filter((s) => s.isAvailable).length;
  const unavailableCount = scheduleData.length - availableCount;
  const dayOffCount      = scheduleData.filter((s) => !s.isBookable).length;
  const conflictDateDetails = useMemo(() => {
    return scheduleData
      .map((session, index) => ({
        date: session.searchCriteria.date,
        displayDate: moment(session.searchCriteria.date).format('MMM DD, YYYY'),
        sessionNumber: index + 1,
        isConflict: session.isBookable && session.availabilityInfo?.available === false && !session.wasCleared,
      }))
      .filter((session) => session.isConflict);
  }, [scheduleData]);
  const conflictCount = conflictDateDetails.length;
  const getTimeForSession = useCallback((session) => {
    if (!session || session.wasCleared) return null;
    return updatedTimes[session.searchCriteria.date] || session.selectedTime || null;
  }, [updatedTimes]);
  const bookableSessions = scheduleData.filter((session) => {
    if (!session.isAvailable || session.wasCleared) return false;
    const timeForSession = getTimeForSession(session);
    return !!(timeForSession?.start && timeForSession?.end);
  });
  const bookableCount = bookableSessions.length;

  const allAvailableHaveTime = useMemo(() => {
    const availableSessions = scheduleData.filter((s) => s.isAvailable && !s.wasCleared);
    if (availableSessions.length === 0) return false;
    
    return availableSessions.every((session) => {
      const timeForSession = getTimeForSession(session);
      return timeForSession?.start && timeForSession?.end;
    });
  }, [scheduleData, getTimeForSession]);

  const missingTimeDates = useMemo(() => {
    const availableSessions = scheduleData.filter((s) => s.isAvailable && !s.wasCleared);
    const missing = availableSessions.filter((session) => {
      const timeForSession = getTimeForSession(session);
      return !timeForSession?.start || !timeForSession?.end;
    });
    return missing.map((s) => moment(s.searchCriteria.date).format('MMM DD'));
  }, [scheduleData, getTimeForSession]);
  const unresolvedConflictDates = useMemo(() => {
    return scheduleData
      .filter((session) => !session.isAvailable && !session.wasCleared)
      .map((session) => moment(session.searchCriteria.date).format('MMM DD'));
  }, [scheduleData]);
  const hasUnresolvedConflicts = unresolvedConflictDates.length > 0;
  const manuallyOmittedSessions = useMemo(() => {
    return scheduleData
      .map((session, index) => ({
        ...session,
        occurrenceNumber: index + 1,
      }))
      .filter((session) => session.wasCleared);
  }, [scheduleData]);

  const totalPrice = useMemo(() => {
    if (!product?.price?.priceAmt) return 0;
    let total = 0;
    scheduleData.forEach((session) => {
      const { isAvailable, wasCleared } = session;
      if (!isAvailable) return;
      if (wasCleared) return;
      const time = getTimeForSession(session);
      if (!time?.duration) return;
      total += parseFloat(product.price.priceAmt) * time.duration;
    });
    return total;
  }, [product, scheduleData, getTimeForSession]);

  const totalOccurrences = occurrenceDates.length;
  const hasAvailableSessions = bookableCount > 0;
  const totalAvailablePrice = totalPrice;
  const removedSessionCount = removedSessions.length + manuallyOmittedSessions.length;
  const latestRemovedSession =
    manuallyOmittedSessions[manuallyOmittedSessions.length - 1] ||
    removedSessions[removedSessions.length - 1] ||
    null;
  const shouldShowRemovedSessionModal = removedSessionCount > 0 && unavailableCount === 0;
  const bookingModalTitle = shouldShowRemovedSessionModal ? 'Session Removed' : 'Some Dates Unavailable';
  const bookingModalPrimaryMessage = shouldShowRemovedSessionModal
    ? removedSessionCount === 1
      ? `You've removed Session ${latestRemovedSession?.occurrenceNumber}. Continue booking the remaining ${formatNumber(bookableCount)} ${bookableCount === 1 ? 'session' : 'sessions'}?`
      : `You've removed ${formatNumber(removedSessionCount)} sessions. Continue booking the remaining ${formatNumber(bookableCount)} ${bookableCount === 1 ? 'session' : 'sessions'}?`
    : `${formatNumber(unavailableCount)} ${unavailableCount === 1 ? 'date has' : 'dates have'} conflicts or are days off.`;
  const bookingModalSecondaryMessage = shouldShowRemovedSessionModal
    ? ''
    : `Continue booking ${formatNumber(bookableCount)} available ${bookableCount === 1 ? 'session' : 'sessions'}?`;
  const bookingModalActionLabel = shouldShowRemovedSessionModal
    ? bookableCount === 1
      ? 'Book 1 Session'
      : `Book ${formatNumber(bookableCount)} Sessions`
    : `Book ${formatNumber(bookableCount)}`;

  const handleBookAll = async () => {
    setBookingError('');

    if (hasUnresolvedConflicts) {
      setShowTimeMissingWarning(true);
      return;
    }

    if (!allAvailableHaveTime) {
      setShowTimeMissingWarning(true);
      return;
    }

    const availableDates = bookableSessions.map((session) => ({
      date:          session.searchCriteria.date,
      time:          getTimeForSession(session),
      originalIndex: occurrenceDates.indexOf(session.searchCriteria.date),
    }));

    if (availableDates.length === 0) {
      setBookingError('No available dates to book.');
      return;
    }

    if (removedSessionCount > 0 || availableDates.length < occurrenceDates.length) {
      setShowBookingModal(true);
    } else {
      await proceedWithBooking(availableDates);
    }
  };

  const proceedWithBooking = async (availableDates) => {
    setIsProcessing(true);
    try {
      navigate('/selected-product-details', {
        state: {
          product, searchCriteria, selectedTime,
          recurrenceData: recurrenceData
            ? { ...recurrenceData, occurrences: availableDates.length }
            : null,
          updatedTimes,
          occurrenceDates: availableDates.map((a) => a.date),
          availabilityData: availabilityData.filter((_, i) =>
            availableDates.some((a) => a.date === occurrenceDates[i]),
          ),
          fromReviewSchedule: true,
          bookingConfirmed:   true,
        },
      });
      setRemovedSessions([]);
    } catch {
      setBookingError('Failed to process booking. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmCancelAll = () => {
    navigate('/selected-product-details', {
      state: {
        ...originalProductData,
        recurrenceData: null,
        updatedTimes:   {},
        occurrenceDates: searchCriteria?.date ? [searchCriteria.date] : [],
        fromReviewSchedule: true,
      },
    });
  };

  const getRecurrenceText = () => {
    if (!recurrenceData) return 'Recurring';
    const { recurrenceType, pattern, interval = 1 } = recurrenceData;
    const type = (recurrenceType || '').toUpperCase();
    if (type === 'DAILY') {
      if (pattern === 'weekday') return 'Every Weekday';
      if (interval === 2)        return 'Every Other Day';
      if (interval === 1)        return 'Daily';
      return `Every ${interval} Days`;
    }
    if (type === 'WEEKLY') {
      if (interval === 2) return 'Biweekly';
      if (interval === 1) return 'Weekly';
      return `Every ${interval} Weeks`;
    }
    if (type === 'MONTHLY') {
      if (interval === 1) return 'Monthly';
      return `Every ${interval} Months`;
    }
    return 'Recurring';
  };

  const getDateRangeText = () =>
    localStartDate && localEndDate
      ? `${formatDate(localStartDate)} – ${formatDate(localEndDate)}`
      : '';

  const recurrenceLabel = getRecurrenceText();

  if (error || !product || !occurrenceDates.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-xl p-12">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              {error ? 'Error Loading Schedule' : 'No Schedule Data'}
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              {error || 'Please go back and select a product with recurrence.'}
            </p>
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 transition-colors font-semibold shadow-lg shadow-blue-200 inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" /> Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">

        <div className="mb-6">
          <motion.button
            whileHover={{ x: -3 }} whileTap={{ scale: 0.98 }}
            onClick={handleBack}
            className="flex items-center text-slate-600 hover:text-slate-900 font-medium transition-colors px-4 py-2.5 rounded-lg hover:bg-white/80 bg-white shadow-sm border border-slate-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Product Details
          </motion.button>
        </div>

        <div className="mb-8 rounded-3xl border border-slate-200 bg-white/90 p-5 sm:p-7 shadow-xl shadow-blue-100/40 backdrop-blur">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700">
              <Calendar className="w-3.5 h-3.5" />
              Recurring Booking Review
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">
              Review Your {recurrenceLabel} Schedule
            </h1>
            <p className="text-base sm:text-lg text-slate-600">
              {formatNumber(totalOccurrences)}{' '}
              {totalOccurrences === 1 ? 'occurrence' : 'occurrences'} of {product.name}
            </p>

            {localStartDate && localEndDate && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 text-sm font-semibold">{getDateRangeText()}</span>
                </div>
                {timeZone && (
                  <div className="bg-slate-100 border border-slate-200 rounded-xl px-4 py-2 inline-flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="text-slate-700 text-sm font-semibold">{timeZone}</span>
                  </div>
                )}
              </div>
            )}

            {recurrenceData && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 inline-block">
                <span className="text-emerald-700 text-sm font-semibold">
                  {recurrenceLabel}
                  {recurrenceData.ends === 'after'
                    ? ` · ${formatNumber(occurrenceDates.length)} occurrences`
                    : ` · until ${moment(recurrenceData.endDate).format('MMM DD, YYYY')}`}
                </span>
              </div>
            )}
          </div>

          <div className="lg:text-right shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 px-5 py-4 text-white shadow-lg shadow-blue-200">
            <div className="text-sm font-semibold text-blue-100">Estimated Total</div>
            <div className="text-3xl font-black">
              {formatCurrency(totalAvailablePrice)}
            </div>
            <div className="text-xs text-blue-100 mt-1">
              {formatNumber(availableCount)} available session{availableCount !== 1 ? 's' : ''} × {formatCurrency(parseFloat(product.price?.priceAmt || 0))}/h
            </div>
          </div>
        </div>
        </div>

        <div className="mb-6 p-5 sm:p-6 bg-white rounded-2xl shadow-sm border border-slate-200">
          <div className={`grid gap-4 ${dayOffCount > 0 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'}`}>
            <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm">
              <div className="text-3xl font-bold text-blue-700">{formatNumber(totalOccurrences)}</div>
              <div className="text-sm text-slate-600 font-medium mt-1">Total Sessions</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100 shadow-sm">
              <div className="text-3xl font-bold text-emerald-700">{formatNumber(availableCount)}</div>
              <div className="text-sm text-slate-600 font-medium mt-1">Available</div>
            </div>
            <div className="relative text-center p-4 bg-amber-50 rounded-xl border border-amber-100 shadow-sm">
              <button
                type="button"
                onClick={() => setShowConflictDates((prev) => !prev)}
                disabled={conflictCount === 0}
                className="absolute right-3 top-3 rounded-full p-1 text-amber-600 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
                title="View conflict dates"
              >
                <Info className="w-4 h-4" />
              </button>
              <div className="text-3xl font-bold text-amber-700">{formatNumber(conflictCount)}</div>
              <div className="text-sm text-slate-600 font-medium mt-1">Conflicts</div>
            </div>
            {dayOffCount > 0 && (
              <div className="text-center p-4 bg-slate-100 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-3xl font-bold text-slate-600">{formatNumber(dayOffCount)}</div>
                <div className="text-sm text-slate-600 font-medium mt-1">Days Off / Holidays</div>
              </div>
            )}
          </div>

          <AnimatePresence>
            {showConflictDates && conflictCount > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
              >
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800">
                  <Info className="w-4 h-4" />
                  Conflict dates
                </div>
                <div className="flex flex-wrap gap-2">
                  {conflictDateDetails.map((session) => (
                    <span
                      key={`${session.date}-${session.sessionNumber}`}
                      className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold text-amber-700"
                    >
                      Session {session.sessionNumber} - {session.displayDate}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mb-6">
          <TimeSlotTips slotInterval={slotIntervalInMinutes} product={product} />
        </div>

        {scheduleData.length > 0 ? (
          <div className="space-y-6">
            {scheduleData.map((schedule, index) => (
              <div key={schedule.uniqueKey} className="relative">
                {!schedule.isBookable && (
                  <div className="absolute -top-2 left-4 z-10">
                    <span className="bg-slate-700 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                      {(() => {
                        const dm = moment.tz(schedule.searchCriteria.date, 'YYYY-MM-DD', timeZone);
                        const isHoliday = holidays.some((h) => {
                          const hd = typeof h === 'string' ? h : h?.date;
                          return hd && moment(hd).isSame(dm, 'day');
                        });
                        return isHoliday ? 'Holiday' : `${dm.format('dddd')} – Day Off`;
                      })()}
                    </span>
                  </div>
                )}

                <RecurrenceProductResultCard
                  product={schedule.product}
                  searchCriteria={schedule.searchCriteria}
                  selectedTime={schedule.selectedTime}
                  onSelect={(data) => handleSelectProduct(data, schedule.searchCriteria.date)}
                  onClearSelection={handleClearSelection}
                  isExpanded={true}
                  occurrenceNumber={index + 1}
                  totalOccurrences={totalOccurrences}
                  isAvailable={schedule.isAvailable}
                  availabilityInfo={schedule.availabilityInfo}
                  isReviewMode={true}
                  onDelete={handleDeleteOccurrence}
                  companyTimezone={timeZone}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Schedule Data Available</h3>
            <p className="text-slate-600">Unable to generate schedule data. Please try again.</p>
          </div>
        )}

        {scheduleData.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="text-center">
              <AnimatePresence>
                {showTimeMissingWarning && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full"
                  >
                    <div className="bg-amber-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">
                          {hasUnresolvedConflicts ? 'Clear Conflicted Sessions First' : 'Missing Time Selections'}
                        </p>
                        <p className="text-sm opacity-90">
                          {hasUnresolvedConflicts
                            ? `Please clear or remove these conflicted sessions first: ${unresolvedConflictDates.join(', ')}`
                            : `Please select a time slot for: ${missingTimeDates.join(', ')}`}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                whileHover={!isProcessing && allAvailableHaveTime && hasAvailableSessions && !hasUnresolvedConflicts ? { scale: 1.02 } : {}}
                whileTap={!isProcessing && allAvailableHaveTime && hasAvailableSessions && !hasUnresolvedConflicts ? { scale: 0.98 } : {}}
                onClick={handleBookAll}
                disabled={isProcessing || !hasAvailableSessions || !allAvailableHaveTime || hasUnresolvedConflicts}
                className={`bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-5 rounded-xl text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-3 mx-auto shadow-blue-200 ${
                  (isProcessing || !hasAvailableSessions || !allAvailableHaveTime || hasUnresolvedConflicts) 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:scale-105'
                }`}
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                    Processing…
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6" />
                    Confirm All {formatNumber(bookableCount)} Available Sessions
                  </>
                )}
              </motion.button>
              
              {hasUnresolvedConflicts && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 p-4 bg-rose-50 border border-rose-200 rounded-xl max-w-md mx-auto"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-rose-800 font-semibold text-sm">
                        Conflicted sessions must be cleared
                      </p>
                      <p className="text-rose-700 text-xs mt-1">
                        These sessions will not be included until you clear or remove them:
                      </p>
                      <p className="text-rose-600 text-xs font-mono mt-1">
                        {unresolvedConflictDates.join(', ')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {hasAvailableSessions && !allAvailableHaveTime && !hasUnresolvedConflicts && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl max-w-md mx-auto"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-amber-800 font-semibold text-sm">
                        Time selection required
                      </p>
                      <p className="text-amber-700 text-xs mt-1">
                        Please select a time slot for the following sessions:
                      </p>
                      <p className="text-amber-600 text-xs font-mono mt-1">
                        {missingTimeDates.join(', ')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {!hasAvailableSessions && (
                <p className="text-amber-600 text-sm mt-3 font-medium">
                  No available sessions to book. Please edit conflicting sessions or remove them.
                </p>
              )}
              <p className="text-slate-500 text-sm mt-3 font-medium">Total: {formatCurrency(totalAvailablePrice)}</p>
            </div>

            <div className="text-center">
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => setShowCancelAllModal(true)}
                disabled={isProcessing}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto shadow-red-200 disabled:opacity-50"
              >
                <X className="w-5 h-5" /> Cancel All Recurring Bookings
              </motion.button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-slate-600 font-medium">
          Showing {formatNumber(scheduleData.length)} of {formatNumber(totalOccurrences)} scheduled sessions
          {unavailableCount > 0 && ` (${formatNumber(unavailableCount)} with conflicts)`}
          {dayOffCount > 0 && ` · ${formatNumber(dayOffCount)} day(s) off / holiday`}
        </div>
      </div>

      <AnimatePresence>
        {showCancelAllModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCancelAllModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Cancel All Bookings?</h3>
                  <p className="text-slate-600 text-sm">
                    Are you sure you want to cancel all {formatNumber(totalOccurrences)} recurring bookings?
                    This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => setShowCancelAllModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                >Keep Bookings</motion.button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={confirmCancelAll}
                  className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-200"
                >Cancel All</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBookingModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBookingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{bookingModalTitle}</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    {bookingModalPrimaryMessage}
                  </p>
                  {bookingModalSecondaryMessage && (
                    <p className="text-slate-700 text-sm font-semibold">
                      {bookingModalSecondaryMessage}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                >Cancel</motion.button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    const availableDates = bookableSessions.map((session) => ({
                      date:          session.searchCriteria.date,
                      time:          getTimeForSession(session),
                      originalIndex: occurrenceDates.indexOf(session.searchCriteria.date),
                    }));
                    proceedWithBooking(availableDates);
                    setShowBookingModal(false);
                  }}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
                >{bookingModalActionLabel}</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bookingError && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-start gap-3 rounded-xl bg-red-600 px-5 py-4 text-white shadow-2xl"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span className="flex-1 text-sm font-semibold">{bookingError}</span>
            <button
              type="button"
              onClick={() => setBookingError('')}
              className="rounded-full p-1 text-white/80 transition hover:bg-white/10 hover:text-white"
              aria-label="Dismiss booking error"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 right-8 bg-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Session deleted successfully!</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewSchedule;
